import React, { useState } from 'react';
import toast from 'react-hot-toast';
import baseUrl from '../../base_url';
import {
  ArrowLeft,
  Upload,
  Plus,
  Trash2,
  X,
  Package
} from 'lucide-react';

const AdminAddProducts = () => {
  const [product, setProduct] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    numericPrice: '',
    brandName: 'Mia Omorfia',
    articleCode: '',
    broadCategory: '',
    status: 'active',
    slug: '',
    inventory: '10'
  });

  const [ingredients, setIngredients] = useState(['']);
  const [allergens, setAllergens] = useState(['']);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  // For first image metadata
  const [firstImageMetadata, setFirstImageMetadata] = useState({
    alt: '',
    dataAltText: ''
  });

  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-') + '-' + (product.broadCategory || 'skincare');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setProduct({ ...product, [name]: checked });
    } else {
      const updatedProduct = { ...product, [name]: value };

      // Update slug when title changes
      if (name === 'title') {
        updatedProduct.slug = generateSlug(value);
      }

      // Update numericPrice when price changes
      if (name === 'price') {
        const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
        updatedProduct.numericPrice = isNaN(numericValue) ? 0 : numericValue;
      }

      setProduct(updatedProduct);
    }
  };

  const handleFirstImageMetadataChange = (e) => {
    const { name, value } = e.target;
    setFirstImageMetadata({ ...firstImageMetadata, [name]: value });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles([...selectedFiles, ...files]);

    const newPreviewImages = files.map(file => URL.createObjectURL(file));
    setPreviewImages([...previewImages, ...newPreviewImages]);
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    URL.revokeObjectURL(previewImages[index]);
    setPreviewImages(previewImages.filter((_, i) => i !== index));
  };

  // Ingredients handlers
  const addIngredient = () => {
    setIngredients([...ingredients, '']);
  };

  const removeIngredient = (index) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((_, i) => i !== index));
    }
  };

  const handleIngredientChange = (index, value) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = value;
    setIngredients(newIngredients);
  };

  // Allergens handlers
  const addAllergen = () => {
    setAllergens([...allergens, '']);
  };

  const removeAllergen = (index) => {
    if (allergens.length > 1) {
      setAllergens(allergens.filter((_, i) => i !== index));
    }
  };

  const handleAllergenChange = (index, value) => {
    const newAllergens = [...allergens];
    newAllergens[index] = value;
    setAllergens(newAllergens);
  };

  const formatPrice = (price) => {
    const num = parseFloat(price.replace(/[^0-9.]/g, ''));
    if (isNaN(num)) return '£0.00';
    return `£${num.toFixed(2)}`;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!product.title || !product.category || !product.price || selectedFiles.length === 0) {
      toast.error('Please fill in all required fields and upload at least one image');
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);
    setUploadStatus('Preparing product data...');

    try {
      const formData = new FormData();
      formData.append('title', product.title);
      formData.append('description', product.description);
      formData.append('category', product.category);
      formData.append('price', product.price.startsWith('£') ? product.price : `£ ${product.price}`);
      formData.append('numericPrice', product.numericPrice || parseFloat(product.price.replace(/[^0-9.]/g, '')) || 0);
      formData.append('brandName', product.brandName);
      formData.append('articleCode', product.articleCode);
      formData.append('broadCategory', product.broadCategory);
      formData.append('status', product.status);
      formData.append('slug', product.slug || generateSlug(product.title));
      formData.append('inventory', product.inventory);

      // Add arrays
      const filteredIngredients = ingredients.filter(i => i.trim());
      filteredIngredients.forEach((ingredient, index) => {
        formData.append(`ingredients[${index}]`, ingredient);
      });

      const filteredAllergens = allergens.filter(a => a.trim());
      filteredAllergens.forEach((allergen, index) => {
        formData.append(`allergens[${index}]`, allergen);
      });

      // Add images
      selectedFiles.forEach((file) => {
        formData.append('image', file);
      });

      // Add first image metadata
      if (firstImageMetadata.alt) {
        formData.append('firstImageAlt', firstImageMetadata.alt);
      }
      if (firstImageMetadata.dataAltText) {
        formData.append('firstImageDataAltText', firstImageMetadata.dataAltText);
      }

      setUploadStatus('Uploading images and saving product...');

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${baseUrl}api/admin/add-product`, true);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
          setUploadStatus(`Uploading: ${percentComplete}%`);
        }
      };

      xhr.onload = () => {
        setIsLoading(false);
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success('Product added to catalog successfully');
          setTimeout(() => {
            window.location.href = '/view-products';
          }, 1000);
        } else {
          try {
            const res = JSON.parse(xhr.responseText);
            toast.error(res.message || 'Failed to save product');
          } catch {
            toast.error('Failed to save product');
          }
        }
      };

      xhr.onerror = () => {
        setIsLoading(false);
        toast.error('Network error while saving product');
      };

      xhr.send(formData);
    } catch (error) {
      setIsLoading(false);
      console.error('Error adding product:', error);
      toast.error('Failed to add product. Please try again.');
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
        <div className="flex items-center gap-3">
          <a
            href="/view-products"
            className="p-2 rounded-lg border border-stone-200 text-stone-500 hover:text-stone-800 hover:bg-stone-50 transition-colors"
            title="Back to Catalog"
          >
            <ArrowLeft className="w-4 h-4" />
          </a>
          <div>
            <h1 className="text-xl font-bold text-stone-900 tracking-tight">Add New Product</h1>
            <p className="text-xs text-stone-500 mt-0.5">
              Create a new SKU listing with specifications, pricing, imagery, and ingredients.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <button
            type="button"
            className="px-3.5 py-2 rounded-lg border border-stone-200 text-stone-700 hover:bg-stone-50 text-xs font-medium transition-colors"
            onClick={() => window.location.href = '/view-products'}
            disabled={isLoading}
          >
            Discard
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 transition-colors shadow-xs ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'Saving Product...' : 'Publish Product'}
          </button>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95 duration-150">
            <h3 className="text-sm font-bold text-stone-900 mb-1">Publishing Product</h3>
            <p className="text-xs text-stone-500 mb-4">{uploadStatus}</p>

            <div className="mb-2 flex justify-between text-xs font-mono">
              <span className="text-stone-500">Progress</span>
              <span className="font-bold text-stone-900">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden">
              <div
                className="bg-amber-600 h-2 rounded-full transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Form Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Columns - Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-stone-200 shadow-sm space-y-4">
            <h2 className="text-sm font-bold text-stone-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-amber-700" />
              Basic Product Details
            </h2>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-stone-700 mb-1">
                  Product Title <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  placeholder="e.g. Luxury Organic Glow Face Mask"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                  value={product.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">
                    Article Code / SKU <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="articleCode"
                    placeholder="e.g. MO-FM-001"
                    className="w-full font-mono rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    value={product.articleCode}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Brand Name</label>
                  <input
                    type="text"
                    name="brandName"
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    value={product.brandName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">
                    Category <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="category"
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500 bg-white"
                    value={product.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="face-masks">Face Masks</option>
                    <option value="serums">Serums</option>
                    <option value="moisturizers">Moisturizers</option>
                    <option value="cleansers">Cleansers</option>
                    <option value="toners">Toners</option>
                    <option value="eye-care">Eye Care</option>
                    <option value="body-care">Body Care</option>
                    <option value="hair-care">Hair Care</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-stone-700 mb-1">
                    Broad Category <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="broadCategory"
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500 bg-white"
                    value={product.broadCategory}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Broad Category</option>
                    <option value="skincare">Skincare</option>
                    <option value="haircare">Haircare</option>
                    <option value="bodycare">Body Care</option>
                    <option value="wellness">Wellness</option>
                    <option value="organic">Organic</option>
                    <option value="ayurvedic">Ayurvedic</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing & Stock */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-stone-200 shadow-sm space-y-4">
            <h2 className="text-sm font-bold text-stone-900 flex items-center gap-2">
              <span className="font-mono text-amber-700">£</span>
              Pricing & Inventory
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <label className="block font-semibold text-stone-700 mb-1">
                  Retail Price (£) <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-stone-400 font-mono">£</span>
                  <input
                    type="text"
                    name="price"
                    placeholder="39.00"
                    className="w-full pl-8 rounded-lg border border-stone-200 px-3 py-2 text-xs font-mono font-semibold focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    value={product.price}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-stone-700 mb-1">Initial Stock Count (Units)</label>
                <input
                  type="number"
                  name="inventory"
                  placeholder="10"
                  min="0"
                  className="w-full font-mono rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                  value={product.inventory}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block font-semibold text-stone-700 mb-1">Listing Status</label>
                <select
                  name="status"
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500 bg-white"
                  value={product.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active (Visible)</option>
                  <option value="inactive">Inactive (Draft)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Product Description */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-stone-200 shadow-sm space-y-4">
            <h2 className="text-sm font-bold text-stone-900">Product Story & Description</h2>

            <div className="text-xs space-y-2">
              <textarea
                name="description"
                rows="6"
                className="w-full rounded-lg border border-stone-200 p-3 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                value={product.description}
                onChange={handleInputChange}
                placeholder="Enter rich product description. You can separate paragraphs with #$# delimiter..."
              />
              <p className="text-[11px] text-stone-400">
                Tip: Use <code className="bg-stone-100 px-1 py-0.5 rounded text-stone-700 font-mono">#$#</code> to split sections into separate customer-facing paragraphs.
              </p>
            </div>
          </div>

          {/* Product Images Upload */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-stone-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-stone-900">Product Photography</h2>
                <p className="text-[11px] text-stone-500">First image will be the primary catalog thumbnail</p>
              </div>
              <span className="text-xs font-mono font-semibold text-stone-500">
                {selectedFiles.length} Selected
              </span>
            </div>

            <div className="border-2 border-dashed border-stone-200 rounded-2xl p-6 text-center hover:border-amber-400 transition-colors bg-stone-50/50">
              <label className="cursor-pointer block">
                <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center mx-auto mb-2">
                  <Upload className="w-5 h-5" />
                </div>
                <span className="inline-block px-3.5 py-1.5 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 transition-colors shadow-xs">
                  Choose Image Files
                </span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </label>
              <p className="text-stone-400 text-[11px] mt-2">JPEG, PNG, WebP supported. Multiple files allowed.</p>
            </div>

            {previewImages.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 pt-2">
                {previewImages.map((src, index) => (
                  <div key={index} className="relative group aspect-square rounded-xl overflow-hidden border border-stone-200 bg-stone-100">
                    <img
                      src={src}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {index === 0 && (
                      <span className="absolute top-1 left-1 bg-amber-700 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                        Main
                      </span>
                    )}
                    <button
                      type="button"
                      className="absolute top-1 right-1 bg-stone-900/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removeFile(index)}
                      title="Remove"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {previewImages.length > 0 && (
              <div className="pt-3 border-t border-stone-100 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="block font-medium text-stone-600 mb-1">Primary Image Alt Text</label>
                  <input
                    type="text"
                    name="alt"
                    placeholder={`e.g. ${product.title || 'Product'} bottle view`}
                    className="w-full rounded-lg border border-stone-200 px-3 py-1.5 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    value={firstImageMetadata.alt}
                    onChange={handleFirstImageMetadataChange}
                  />
                </div>
                <div>
                  <label className="block font-medium text-stone-600 mb-1">Accessibility Caption</label>
                  <input
                    type="text"
                    name="dataAltText"
                    placeholder={`e.g. ${product.title || 'Product'} organic skincare`}
                    className="w-full rounded-lg border border-stone-200 px-3 py-1.5 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    value={firstImageMetadata.dataAltText}
                    onChange={handleFirstImageMetadataChange}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Ingredients & Allergens */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-stone-200 shadow-sm space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-stone-900">Key Ingredients</h2>
                <button
                  type="button"
                  onClick={addIngredient}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:underline"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Ingredient
                </button>
              </div>

              <div className="space-y-2">
                {ingredients.map((ingredient, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      className="flex-1 rounded-lg border border-stone-200 px-3 py-1.5 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                      value={ingredient}
                      onChange={(e) => handleIngredientChange(index, e.target.value)}
                      placeholder="e.g. Aloe Vera Leaf Extract, Neem Powder"
                    />
                    {ingredients.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeIngredient(index)}
                        className="text-stone-400 hover:text-rose-600 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-stone-100">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-stone-900">Allergen Declarations</h2>
                <button
                  type="button"
                  onClick={addAllergen}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:underline"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Allergen
                </button>
              </div>

              <div className="space-y-2">
                {allergens.map((allergen, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      className="flex-1 rounded-lg border border-stone-200 px-3 py-1.5 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                      value={allergen}
                      onChange={(e) => handleAllergenChange(index, e.target.value)}
                      placeholder="e.g. Tree Nuts, Essential Oils"
                    />
                    {allergens.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAllergen(index)}
                        className="text-stone-400 hover:text-rose-600 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Summary Cockpit */}
        <div className="lg:col-span-1">
          <div className="bg-white p-5 rounded-2xl border border-stone-200 shadow-sm sticky top-6 space-y-4 text-xs">
            <h2 className="text-sm font-bold text-stone-900 border-b border-stone-100 pb-3">
              Listing Summary
            </h2>

            <div className="space-y-3">
              <div>
                <span className="text-stone-400 block text-[11px]">Title</span>
                <span className="font-semibold text-stone-900 truncate block">
                  {product.title || 'Not specified'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-stone-400 block text-[11px]">SKU</span>
                  <span className="font-mono text-stone-800">{product.articleCode || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-stone-400 block text-[11px]">Price</span>
                  <span className="font-mono font-bold text-stone-900">
                    {product.price ? formatPrice(product.price) : '£0.00'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-stone-400 block text-[11px]">Category</span>
                  <span className="text-stone-800">{product.category || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-stone-400 block text-[11px]">Stock</span>
                  <span className="font-mono font-semibold text-stone-800">{product.inventory || '0'} units</span>
                </div>
              </div>

              <div>
                <span className="text-stone-400 block text-[11px]">Status</span>
                <span className={`inline-flex items-center gap-1 font-semibold ${
                  product.status === 'active' ? 'text-emerald-700' : 'text-stone-600'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    product.status === 'active' ? 'bg-emerald-500' : 'bg-stone-400'
                  }`} />
                  {product.status === 'active' ? 'Active on store' : 'Draft / Hidden'}
                </span>
              </div>

              <div>
                <span className="text-stone-400 block text-[11px]">SEO URL Slug</span>
                <span className="font-mono text-[11px] text-stone-600 break-all block bg-stone-50 p-1.5 rounded border border-stone-100">
                  {product.slug || generateSlug(product.title || 'new-product')}
                </span>
              </div>
            </div>

            <div className="pt-3 border-t border-stone-100">
              <button
                type="button"
                className={`w-full py-2.5 rounded-lg bg-stone-900 text-white font-semibold text-xs hover:bg-stone-800 transition-colors shadow-xs ${
                  isLoading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Publish Product'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAddProducts;